import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Tag,
  Image,
  Space,
  Typography,
  Popconfirm,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
  TagOutlined,
  UserOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Roles considered as "bureau" (office staff)
const BUREAU_ROLES = ['BUREAU'];

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    fetchTickets();
    fetchEmployees();

    // Realtime subscription
    const subscription = supabase
      .channel('tickets_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tickets' }, (payload) => {
        setTickets(prev => [payload.new, ...prev]);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tickets' }, (payload) => {
        setTickets(prev => prev.map(t => t.id === payload.new.id ? payload.new : t));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'tickets' }, (payload) => {
        setTickets(prev => prev.filter(t => t.id !== payload.old.id));
      })
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      message.error('Erreur lors du chargement des tickets');
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, first_name, role')
        .in('role', BUREAU_ROLES)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleAdd = () => {
    setEditingTicket(null);
    form.resetFields();
    setFileList([]);
    setModalVisible(true);
  };

  const handleEdit = (ticket) => {
    setEditingTicket(ticket);
    form.setFieldsValue({
      title: ticket.title,
      description: ticket.description,
      author_id: ticket.author_id,
      status: ticket.status,
    });
    // Convert existing images to fileList format
    if (ticket.images && ticket.images.length > 0) {
      setFileList(ticket.images.map((url, index) => ({
        uid: `-${index}`,
        name: `image-${index}`,
        status: 'done',
        url: url,
      })));
    } else {
      setFileList([]);
    }
    setModalVisible(true);
  };

  const handleDelete = async (ticketId) => {
    try {
      // First, delete images from storage if any
      const ticket = tickets.find(t => t.id === ticketId);
      if (ticket?.images) {
        for (const imageUrl of ticket.images) {
          const path = imageUrl.split('/').pop();
          await supabase.storage.from('tickets').remove([`images/${path}`]);
        }
      }

      const { error } = await supabase.from('tickets').delete().eq('id', ticketId);
      if (error) throw error;

      setTickets(prev => prev.filter(t => t.id !== ticketId));
      message.success('Ticket supprimé');
    } catch (error) {
      console.error('Error deleting ticket:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const uploadImage = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    const filePath = `images/${fileName}`;

    const { error } = await supabase.storage
      .from('tickets')
      .upload(filePath, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('tickets')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Upload new images
      const imageUrls = [];
      for (const file of fileList) {
        if (file.originFileObj) {
          // New file to upload
          const url = await uploadImage(file.originFileObj);
          imageUrls.push(url);
        } else if (file.url) {
          // Existing image
          imageUrls.push(file.url);
        }
      }

      const author = employees.find(e => e.id === values.author_id);
      const ticketData = {
        title: values.title,
        description: values.description || '',
        author_id: values.author_id || null,
        author_name: author ? `${author.name}${author.first_name ? ' ' + author.first_name : ''}` : 'Unknown',
        images: imageUrls,
        status: values.status || 'open',
      };

      console.log('Saving ticket data:', ticketData);

      if (editingTicket) {
        const { error } = await supabase
          .from('tickets')
          .update({ ...ticketData, updated_at: new Date().toISOString() })
          .eq('id', editingTicket.id);

        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }
        message.success('Ticket modifié');
      } else {
        const { data, error } = await supabase
          .from('tickets')
          .insert([ticketData])
          .select();

        if (error) {
          console.error('Supabase insert error:', error);
          throw error;
        }
        console.log('Ticket created:', data);
        message.success('Ticket créé');
      }

      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchTickets();
    } catch (error) {
      console.error('Error saving ticket:', error);
      message.error(`Erreur: ${error.message || 'Erreur lors de la sauvegarde'}`);
    }
  };

  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setPreviewOpen(true);
  };

  const uploadProps = {
    listType: 'picture-card',
    fileList,
    onPreview: handlePreview,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList),
    beforeUpload: () => false, // Prevent auto upload
    accept: 'image/*',
  };

  const statusColors = {
    open: { color: '#1890ff', label: 'Ouvert' },
    in_progress: { color: '#faad14', label: 'En cours' },
    resolved: { color: '#52c41a', label: 'Résolu' },
    closed: { color: '#8c8c8c', label: 'Fermé' },
  };

  const columns = [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (title, record) => (
        <div>
          <Text strong style={{ fontSize: '14px', display: 'block' }}>{title}</Text>
          {record.description && (
            <Paragraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0, color: '#666', fontSize: '12px' }}
            >
              {record.description}
            </Paragraph>
          )}
        </div>
      ),
    },
    {
      title: 'Images',
      dataIndex: 'images',
      key: 'images',
      width: 150,
      render: (images) => (
        <Image.PreviewGroup>
          <Space size={4}>
            {images && images.length > 0 ? (
              images.slice(0, 3).map((url, idx) => (
                <Image
                  key={idx}
                  src={url}
                  width={40}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: '4px' }}
                />
              ))
            ) : (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <FileImageOutlined /> Aucune
              </Text>
            )}
            {images && images.length > 3 && (
              <Tag>+{images.length - 3}</Tag>
            )}
          </Space>
        </Image.PreviewGroup>
      ),
    },
    {
      title: 'Auteur',
      dataIndex: 'author_name',
      key: 'author_name',
      width: 150,
      render: (name) => (
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = statusColors[status] || statusColors.open;
        return (
          <Tag color={config.color} style={{ borderRadius: '12px' }}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Créé',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {dayjs(date).fromNow()}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#1890ff' }}
          />
          <Popconfirm
            title="Supprimer ce ticket ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: colors.bgPrimary, padding: spacing.xxl }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.xl,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #722ed1, #9254de)',
              borderRadius: radius.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '24px',
            }}>
              <TagOutlined />
            </div>
            <div>
              <Title level={3} style={{ margin: 0 }}>Tickets</Title>
              <Text type="secondary">N'hésitez pas à ouvrir un ticket pour toute question ou bug lié au CRM</Text>
            </div>
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
            style={{
              background: '#722ed1',
              borderColor: '#722ed1',
              borderRadius: radius.lg,
            }}
          >
            Nouveau Ticket
          </Button>
        </div>

        {/* Table */}
        <div style={{
          background: '#fff',
          borderRadius: radius.xl,
          padding: spacing.lg,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <Table
            columns={columns}
            dataSource={tickets}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aucun ticket"
                >
                  <Button type="primary" onClick={handleAdd}>
                    Créer un ticket
                  </Button>
                </Empty>
              ),
            }}
          />
        </div>

        {/* Modal */}
        <Modal
          title={
            <Space>
              <TagOutlined style={{ color: '#722ed1' }} />
              {editingTicket ? 'Modifier le ticket' : 'Nouveau ticket'}
            </Space>
          }
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
            setFileList([]);
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              setModalVisible(false);
              form.resetFields();
              setFileList([]);
            }}>
              Annuler
            </Button>,
            <Button
              key="save"
              type="primary"
              onClick={handleSave}
              style={{ background: '#722ed1', borderColor: '#722ed1' }}
            >
              {editingTicket ? 'Modifier' : 'Créer'}
            </Button>,
          ]}
          width={600}
        >
          <Form form={form} layout="vertical" style={{ marginTop: spacing.lg }}>
            <Form.Item
              name="title"
              label="Titre"
              rules={[{ required: true, message: 'Le titre est requis' }]}
            >
              <Input placeholder="Titre du ticket" size="large" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <TextArea
                rows={4}
                placeholder="Décrivez le problème ou la demande..."
              />
            </Form.Item>

            <Form.Item
              name="author_id"
              label="Auteur"
              rules={[{ required: true, message: "L'auteur est requis" }]}
            >
              <Select placeholder="Sélectionner un auteur" size="large" showSearch optionFilterProp="children">
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.name} {emp.first_name || ''} ({emp.role})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="status" label="Statut" initialValue="open">
              <Select size="large">
                <Option value="open">Ouvert</Option>
                <Option value="in_progress">En cours</Option>
                <Option value="resolved">Résolu</Option>
                <Option value="closed">Fermé</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Images">
              <Upload {...uploadProps}>
                {fileList.length >= 5 ? null : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Ajouter</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Form>
        </Modal>

        {/* Image Preview */}
        <Modal
          open={previewOpen}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </div>
  );
};

export default Tickets;
